import { ThemeProvider, StyledEngineProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Axios from 'axios';
import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Alert from './components/Alert';
import Navbar from './components/Navbar';
import Message from './components/Message';
import NavDrawer from './components/NavDrawer';
import AccountList from './pages/Accounts';
import ClientList from './pages/Clients';
import Documents from './pages/Documents';
import Account from './pages/Account';
import Client from './pages/Client';
import Dashboard from './pages/Dashboard';
import Search from './pages/Search';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import SetPassword from './pages/SetPassword';
import ErrorBoundary from './components/ErrorBoundary';
import Logout from './pages/Logout';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import NotFound from './components/NotFound';
import { setNotFound } from './redux/slices/error';
import { showAlert } from './redux/slices/alerts';
import { isLoggedIn, setForgotPassword, logOut, setIsLogged } from './redux/slices/auth';
import { getConfig, getConfigValue, setContentFilter } from './redux/slices/config';
import { getCalendarStatus } from './redux/slices/calendar';
// import { toggleTheme } from './redux/slices/ui';
import { lightTheme, darkTheme } from './theme';
import styled from '@emotion/styled';
import ProjectPage from './pages/Project';
import TaskBoard from './pages/TaskBoard';
import MasterReport from './pages/MasterReport';  
import RunnitReports from './pages/RunnitReports';
import Billing from './pages/Billing';
import InvoiceView from './components/InvoiceView';
import ClientInvoiceView from './components/ClientInvoiceView';
import ClientBillingOverview from './pages/ClientBillingOverview';
import { useHasPermission } from './utils/permissionUtils';
import { useBrandName, rememberBrandName } from './utils/brandUtils';
import { isStaleColumnError } from './utils/kanbanColumns';
import useSocket from './hooks/useSocket';
import { useEnabledPlugins, useInternalPlugins } from './clientPlugins';
import store from './redux/configureStore';
import TimerBar from './components/TimerBar';
import AskAIChat from './components/AskAIChat';
import Ask from './pages/Ask';
import Assignments from './pages/Assignments';
import MyHours from './pages/MyHours';
import UnassignedTriage from './pages/UnassignedTriage';
import Templates from './pages/Templates';
import Tags from './pages/Tags';
import KnowledgeBase from './pages/KnowledgeBase';
import Quotes from './pages/Quotes';
import ClientQuotes from './pages/ClientQuotes';
import Meetings from './pages/Meetings';
import InboundMail from './pages/InboundMail';
import Suggestions from './pages/Suggestions';
import MyNotifications from './pages/MyNotifications';
// Admin pages
import AdminPermissions from './pages/admin/AdminPermissions';
import AdminSkills from './pages/admin/AdminSkills';
import AdminServiceAccounts from './pages/admin/AdminServiceAccounts';
import AdminApiKeys from './pages/admin/AdminApiKeys';
import AdminUserAccess from './pages/admin/AdminUserAccess';
import AdminRoles from './pages/admin/AdminRoles';
import AdminRoleDashboards from './pages/admin/AdminRoleDashboards';
import AdminDepartments from './pages/admin/AdminDepartments';
import AdminHolidays from './pages/admin/AdminHolidays';
import AdminPlugins from './pages/admin/AdminPlugins';
import Notifications from './pages/admin/config/Notifications';
import NotificationTemplates from './pages/admin/config/NotificationTemplates';
import SystemConfig from './pages/admin/config/SystemConfig';
import StorageConfig from './pages/admin/config/StorageConfig';
import BillingConfig from './pages/admin/config/BillingConfig';
import InvoicePdfTemplate from './pages/admin/config/InvoicePdfTemplate';
import AIConfig from './pages/admin/config/AIConfig';
import ContentFilter from './pages/admin/config/ContentFilter';
import EscalationSettings from './pages/admin/config/EscalationSettings';
import WhatsAppSettings from './pages/admin/config/WhatsAppSettings';
import InboundMailConfig from './pages/admin/config/InboundMailConfig';
import BackupRestore from './pages/admin/config/BackupRestore';

const StyledBody = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  [theme.breakpoints.up('sm')]: {
    alignItems: 'stretch'
  }
}));

const StyledContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  width: '100%',
}));

const StyledMainContent = styled('div', { shouldForwardProp: (prop) => prop !== '$timerBar' && prop !== '$launcher' })(({ theme, $timerBar, $launcher }) => {
  const paddingBottom = `${($launcher ? 88 : 64) + ($timerBar ? 64 : 0)}px`;
  return {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 1),
    alignItems: 'center',
    flex: '1 1 auto',
    minWidth: 0,
    paddingBottom,
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 3),
      paddingBottom,
    }
  };
});

// Create a function to setup the interceptor that takes necessary dependencies
const setupAxiosInterceptors = (store) => {
  Axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      const status = error.response?.status;
      const requestUrl = error.config?.url || '';
      const isMfaVerify = requestUrl.endsWith('/auth/2fa/verify');
      if (isMfaVerify) {
        return Promise.reject(error);
      }
      // A 401/403 from a login endpoint means "this attempt was rejected", not "session expired" —
      // let the auth sagas surface it instead of logging the visitor out or dropping them out of the flow.
      const isLoginAttempt = requestUrl.endsWith('/auth/login') || requestUrl.endsWith('/auth/passwordless-login');
      if ((status === 401 || status === 403) && isLoginAttempt) {
        return Promise.reject(error);
      }
      if (status === 401) {
        if (store.getState().auth.isLogged !== false) {
          store.dispatch(showAlert({ severity: "warning", message: "Your session has expired, You've been logged out" }));
          store.dispatch(logOut());
          store.dispatch(setIsLogged({ isLogged: false }));
        }
      } else if (status === 404) {
        const failingPath = (error.config?.url || '').split('?')[0].replace(/\/$/, '');
        const currentTail = window.location.pathname.split('/').filter(Boolean).pop() || '';
        if (currentTail && failingPath.endsWith('/' + currentTail)) {
          store.dispatch(setNotFound(true));
        }
      } else if (status === 409 || isStaleColumnError(error)) {
        // A conflict is a state the caller is expected to explain in context, not a generic failure.
      } else if (!error.response) {
        store.dispatch(showAlert({ severity: "error", message: "Network error — please check your connection" }));
      } else {
        const forgotPassword = store.getState().auth.forgotPassword;
        if (forgotPassword > 0) {
          store.dispatch(setForgotPassword({ step: 0 }));
        }

        let errorMessage = "An error occurred";
        if (typeof error.response?.data?.msg === "string") {
          errorMessage = error.response.data.msg;
        } else if (typeof error.response?.data?.message === "string") {
          errorMessage = error.response.data.message;
        } else if (typeof error.message === "string") {
          errorMessage = error.message;
        }
        store.dispatch(showAlert({ severity: "error", message: errorMessage }));
      }
      return Promise.reject(error);
    }
  );
};

// Initialize the interceptor
setupAxiosInterceptors(store);

const App = () => {
  const themeMode = useSelector((state) => state.ui.theme.mode);
  const dispatch = useDispatch();
  const hasPermission = useHasPermission();
  const isLogged = useSelector(state => state.auth.isLogged);
  const forgotPassword = useSelector(state => state.auth.forgotPassword);
  const newRegistration = useSelector(state => state.auth.newRegistration);
  const mustSetPassword = useSelector(state => state.auth.mustSetPassword);
  const alerts = useSelector(state => state.alerts);
  const notFound = useSelector(state => state.errors.notFound);
  const timerBarVisible = useSelector(state => state.ui.timeTracking);
  const enabledPlugins = useEnabledPlugins();
  const internalPlugins = useInternalPlugins();
  const brandName = useBrandName();
  const currentTheme = themeMode === 'light' ? lightTheme : darkTheme;

  useSocket();

  const location = useLocation();
  useEffect(() => {
    if (notFound) {
      dispatch(setNotFound(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    dispatch(isLoggedIn());
  }, [dispatch]);

  useEffect(() => {
    if (isLogged === null) return;
    dispatch(getConfig());
  }, [isLogged, dispatch]);

  useEffect(() => {
    rememberBrandName(brandName);
  }, [brandName]);

  useEffect(() => {
    if (isLogged && hasPermission('canSeeInternal')) {
      dispatch(getCalendarStatus());
    }
  }, [isLogged, dispatch, hasPermission]);

  useEffect(() => {
    if (isLogged) {
      dispatch(getConfigValue({
        key: 'contentFilter',
        value: null,
        silent: true,
        callback: (data) => dispatch(setContentFilter(data?.value || null)),
      }));
    }
  }, [isLogged, dispatch]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StyledBody>
          {isLogged && mustSetPassword && <SetPassword />}
          {isLogged && !mustSetPassword && (
            <>
              <Navbar />
              <StyledContent>
                <NavDrawer />
                <StyledMainContent $timerBar={timerBarVisible} $launcher={hasPermission('canSeeInternal')}>
                  <Message />
                  {notFound ? (
                    <NotFound />
                  ) : (
                    <ErrorBoundary resetKey={location.pathname}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/notifications" element={<MyNotifications />} />
                      {!hasPermission('canSeeInternal') && <Route path="/my-quotes" element={<ClientQuotes />} />}
                      {!hasPermission('canSeeInternal') && <Route path="/my-quotes/:quoteId" element={<ClientQuotes />} />}
                      {!hasPermission('canSeeInternal') && <Route path="/my-billing" element={<ClientBillingOverview />} />}
                      <Route path="/profile" element={<Profile key="own-profile" />} />
                      <Route path="/settings" element={<Settings key="settings" />} />
                      <Route path="/user/:userId" element={<Profile key="user-profile" />} />
                      <Route path="/clients" element={<ClientList />} />
                      {hasPermission('listClients') && hasPermission('canSeeInternal') && <Route path="/documents" element={<Documents />} />}
                      <Route path="/client/:clientSlug" element={<Client />} />
                      <Route path="/client/:clientSlug/accounts" element={<AccountList client={true} />} />
                      <Route path="/client/:clientSlug/invoices/:invoiceId" element={<ClientInvoiceView />} />
                      <Route path="/accounts" element={<AccountList />} />
                      <Route path="/account/:accountSlug" element={<Account />} />
                      <Route path="/account/:accountSlug/:tab" element={<Account />} />
                      <Route path="/account/:accountSlug/project/:projectId" element={<ProjectPage pinned={true} />} />
                      <Route path="/account/:accountSlug/project/:projectId/task/:taskId/board" element={<TaskBoard />} />
                      {hasPermission("canRunReports") && <Route path="/master-report" element={<MasterReport />} />}
                      {hasPermission("canRunReports") && (
                        <>
                          <Route path="/runnit-reports" element={<Navigate to="/runnit-reports/retainer" replace />} />
                          <Route path="/runnit-reports/:tab" element={<RunnitReports />} />
                        </>
                      )}
                      {hasPermission('canRunBilling') && (
                        <>
                          <Route path="/billing" element={<Navigate to="/billing/overview" replace />} />
                          <Route path="/billing/:tab" element={<Billing />} />
                          <Route path="/billing/invoices/:id" element={<InvoiceView />} />
                          <Route path="/billing/client/:clientId" element={<ClientBillingOverview />} />
                        </>
                      )}
                      {hasPermission('canSeeInternal') && (<Route path="/accounts/pinned" element={<AccountList pinned={true} />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/ask" element={<Ask />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/assignments" element={<Assignments />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/my-hours" element={<MyHours />} />)}
                      {hasPermission('canSeeManagement') && (<Route path="/unassigned-triage" element={<UnassignedTriage />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/meetings" element={<Meetings />} />)}
                      {hasPermission('viewTemplates') && (<Route path="/templates" element={<Templates />} />)}
                      {hasPermission('tagManagement') && (<Route path="/tags" element={<Tags />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/kb" element={<KnowledgeBase />} />)}
                      {hasPermission('canSeeInternal') && (<Route path="/kb/:articleId" element={<KnowledgeBase />} />)}
                      {hasPermission('canManageQuotes') && (<Route path="/quotes" element={<Quotes />} />)}
                      <Route path="/quotes/:quoteId" element={hasPermission('canManageQuotes') ? <Quotes /> : <ClientQuotes />} />
                      {hasPermission('canManageInbox') && (<Route path="/inbound-mail" element={<InboundMail />} />)}
                      {hasPermission('canSeeSuggestions') && (<Route path="/ai-actions" element={<Suggestions />} />)}
                      {hasPermission('canRunReports') && (<Route path="/master-report" element={<MasterReport />} />)}
                      {enabledPlugins.flatMap((plugin) =>
                        plugin.routes.map((route) => (
                          <Route key={`${plugin.id}:${route.path}`} path={route.path} element={<Suspense fallback={null}>{route.element}</Suspense>} />
                        ))
                      )}
                      {internalPlugins.flatMap((plugin) =>
                        plugin.internal.routes.map((route) => (
                          <Route key={`${plugin.id}:internal:${route.path}`} path={route.path} element={<Suspense fallback={null}>{route.element}</Suspense>} />
                        ))
                      )}

                      {/* Admin routes */}
                      {hasPermission('canSeeAdmin') && (
                        <>
                          <Route path="/admin" element={<Navigate to="/admin/config/notifications" replace />} />

                          {/* Configuration Section */}
                          <Route path="/admin/config" element={<Navigate to="/admin/config/notifications" replace />} />
                          <Route path="/admin/config/notifications" element={<Notifications />} />
                          <Route path="/admin/config/notification-templates" element={<NotificationTemplates />} />
                          <Route path="/admin/config/system" element={<SystemConfig />} />
                          <Route path="/admin/config/storage" element={<StorageConfig />} />
                          <Route path="/admin/config/billing" element={<BillingConfig />} />
                          <Route path="/admin/config/invoice-pdf-template" element={<InvoicePdfTemplate />} />
                          <Route path="/admin/config/ai" element={<AIConfig />} />
                          <Route path="/admin/config/content-filter" element={<ContentFilter />} />
                          <Route path="/admin/config/escalation" element={<EscalationSettings />} />
                          <Route path="/admin/config/whatsapp" element={<WhatsAppSettings />} />
                          <Route path="/admin/config/inbound-mail" element={<InboundMailConfig />} />
                          <Route path="/admin/config/backup" element={<BackupRestore />} />

                          {/* Other Admin Sections */}
                          <Route path="/admin/departments" element={<AdminDepartments />} />
                          <Route path="/admin/holidays" element={<AdminHolidays />} />
                          <Route path="/admin/roles" element={<AdminRoles />} />
                          <Route path="/admin/role-dashboards" element={<AdminRoleDashboards />} />
                          <Route path="/admin/permissions" element={<AdminPermissions />} />
                          <Route path="/admin/skills" element={<AdminSkills />} />
                          <Route path="/admin/service-accounts" element={<AdminServiceAccounts />} />
                          <Route path="/admin/api-keys" element={<AdminApiKeys />} />
                          <Route path="/admin/user-access" element={<AdminUserAccess />} />
                          <Route path="/admin/plugins" element={<AdminPlugins />} />
                          <Route path="/admin/plugins/:pluginId" element={<AdminPlugins />} />
                        </>
                      )}

                      <Route path="/logout" element={<Logout />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    </ErrorBoundary>
                  )}
                  <TimerBar />
                  {hasPermission('canSeeInternal') && <AskAIChat />}
                </StyledMainContent>
              </StyledContent>
            </>
          )}
          {isLogged === false && (
            <>
              {forgotPassword === 0 && newRegistration === 0 && <Login />}
              {newRegistration > 0 && <Register />}
              {forgotPassword > 0 && <ForgotPassword />}
            </>
          )}
          <Alert open={alerts.alertOpen} severity={alerts.alertSeverity}>
            {alerts.alertMessage}
          </Alert>
        </StyledBody>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;